import { AlertTriangle, Check, ExternalLink, RefreshCw, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";

import { Github } from "@/components/icons/github";
import { Button } from "@/components/ui/button";
import {
  type GitHubConnectionStatus,
  isApiError,
  useDisconnectGithub,
  useGithubConnectUrl,
  useGithubStatus,
} from "@/lib/api";
import { logger } from "@/lib/logger";

interface GitHubConnectBannerProps {
  onStatusChange: (status: GitHubConnectionStatus | null) => void;
}

export function GitHubConnectBanner({ onStatusChange }: GitHubConnectBannerProps) {
  const { data: status, isLoading: loading, isError } = useGithubStatus();
  const connectMutation = useGithubConnectUrl();
  const disconnectMutation = useDisconnectGithub();
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const disconnecting = disconnectMutation.isPending;

  // Surface the latest status to the parent gate. Prefer last-known-good data:
  // a transient /github/status poll failure shouldn't report null upward (which
  // would gate creation / swap out ProjectList) while the banner itself still
  // shows "Connected" from retained data. Only report null with no cached data.
  useEffect(() => {
    if (status !== undefined) {
      onStatusChange(status);
    } else if (isError) {
      onStatusChange(null);
    }
  }, [status, isError, onStatusChange]);

  const handleConnect = async () => {
    setConnecting(true);
    setConnectError(null);
    try {
      const result = await connectMutation.mutateAsync();
      if (result?.url) {
        window.location.href = result.url;
      } else {
        throw new Error("missing connect url");
      }
    } catch (err: unknown) {
      logger.error("[github-banner] failed to get connect URL:", err);
      const message =
        isApiError(err) && err.status === 503
          ? "GitHub OAuth is not configured yet. Ask an admin to set GITHUB_CLIENT_ID and GITHUB_CLIENT_SECRET."
          : "Failed to connect to GitHub. Please try again.";
      setConnectError(message);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync();
      onStatusChange({
        connected: false,
        github_username: null,
        github_org: status?.github_org ?? "",
        oauth_configured: status?.oauth_configured ?? true,
        two_factor_blocked: false,
      });
    } catch (err) {
      logger.error("[github-banner] failed to disconnect:", err);
    }
  };

  if (loading) {
    return (
      <div className="border-border bg-card rounded-lg border p-4">
        <p className="text-muted-foreground text-sm">Checking GitHub connection...</p>
      </div>
    );
  }

  // A keyless instance (no GitHub OAuth app mounted, e.g. test) can never
  // connect or reconnect anyone, so don't offer to: say what this instance
  // is for instead. Checked before the connection state on purpose -- a
  // stale stored connection would otherwise surface as "reconnect".
  if (status && status.oauth_configured === false) {
    return (
      <div className="border-border bg-card rounded-lg border p-4">
        <h3 className="text-sm font-bold">This instance cannot connect GitHub</h3>
        <p className="text-muted-foreground mt-0.5 text-sm">
          GitHub OAuth is not set up on this Scaffold UI instance, so it cannot create or manage
          projects. It exists to verify a deployment (sign-in, health, version); projects live on
          the production Scaffold UI.
        </p>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="bg-card rounded-lg border p-5">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-bold">GitHub account not connected</h3>
            <p className="text-muted-foreground mt-0.5 text-sm">
              Connect your GitHub account to create and manage scaffold projects.
            </p>
          </div>
          <Button size="sm" onClick={handleConnect} disabled={connecting} className="shrink-0">
            <Github className="mr-2 size-4" />
            {connecting ? "Connecting..." : "Connect GitHub"}
          </Button>
        </div>
        {connectError && <p className="text-destructive mt-3 text-sm">{connectError}</p>}
      </div>
    );
  }

  if (status.needs_reconnect) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-bold text-amber-900 dark:text-amber-100">
              Reconnect GitHub to restore access
            </h3>
            <p className="mt-0.5 text-sm text-amber-800 dark:text-amber-200">
              Your GitHub connection is no longer valid — it expired, was revoked, or is missing the
              permissions we need to confirm your membership in the {status.github_org}{" "}
              organization. Please disconnect and reconnect — no other action is needed.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleConnect}
            disabled={connecting}
            className="shrink-0"
          >
            <RefreshCw className="mr-2 size-4" />
            {connecting ? "Reconnecting..." : "Reconnect GitHub"}
          </Button>
        </div>
        {connectError && <p className="text-destructive mt-3 text-sm">{connectError}</p>}
      </div>
    );
  }

  if (status.two_factor_blocked) {
    return (
      <div className="border-destructive/40 bg-destructive/5 dark:bg-destructive/10 rounded-lg border p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="text-destructive mt-0.5 size-5 shrink-0" />
          <div className="flex-1">
            <h3 className="text-destructive text-sm font-bold">
              GitHub 2FA settings not compliant
            </h3>
            <p className="text-muted-foreground mt-1 text-sm">
              The {status.github_org} org only allows secure two-factor methods. Remove SMS as a 2FA
              method in your GitHub settings and use an authenticator app, passkey, or security key
              instead. Save your recovery codes before making changes.
            </p>
            <a
              href="https://github.com/settings/security"
              target="_blank"
              rel="noopener noreferrer"
              className="text-destructive mt-2 inline-flex items-center gap-1 text-sm font-medium underline"
            >
              Open GitHub security settings
              <ExternalLink className="size-3.5" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (status.has_org_access === false) {
    return (
      <div className="border-destructive/40 bg-destructive/5 dark:bg-destructive/10 rounded-lg border p-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="text-destructive size-4" />
          <span className="text-destructive text-sm">
            Connected as <span className="font-medium">@{status.github_username}</span>, but you are
            not a member of the {status.github_org} GitHub organization.
          </span>
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="text-destructive hover:text-destructive/80 ml-auto text-xs underline"
          >
            {disconnecting ? "Disconnecting..." : "Disconnect"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950">
      <div className="flex items-center gap-2">
        <Check className="size-4 text-green-600 dark:text-green-400" />
        <span className="text-sm text-green-700 dark:text-green-300">
          Connected as <span className="font-medium">@{status.github_username}</span>
        </span>
        <button
          onClick={handleDisconnect}
          disabled={disconnecting}
          className="ml-auto text-xs text-green-600 underline hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
        >
          {disconnecting ? "Disconnecting..." : "Disconnect"}
        </button>
      </div>
    </div>
  );
}
